import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { BadgeCheck, ChevronLeft } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchAllVerifications, type AdminVerification } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'

const KINDS = ['identity', 'org', 'notable'] as const

export function VerificationStats() {
  const t = useT()
  const { can } = useAuth()
  const [rows, setRows] = useState<AdminVerification[] | null>(null)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    fetchAllVerifications(ac.signal)
      .then((r) => alive && setRows(r))
      .catch(() => {})
    return () => {
      alive = false
      ac.abort()
    }
  }, [])

  const agg = useMemo(() => {
    const r = rows ?? []
    const by = (pred: (x: AdminVerification) => boolean) => r.filter(pred).length
    return {
      total: r.length,
      pending: by((x) => x.status === 'pending'),
      approved: by((x) => x.status === 'approved'),
      rejected: by((x) => x.status === 'rejected'),
      byKind: KINDS.map((k) => ({ kind: k, count: by((x) => x.kind === k) })),
    }
  }, [rows])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />
  const loading = rows === null

  const kindLabel: Record<string, string> = {
    identity: t.verif.kindIdentity,
    org: t.verif.kindOrg,
    notable: t.verif.kindNotable,
  }

  return (
    <>
      <PageHeader
        title={t.verif.statsTitle}
        screenId="A9.14"
        actions={
          <Link
            to="/verification"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.verif.queue}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="mb-4 flex flex-wrap gap-3">
          <Tile label={t.verif.total} value={agg.total} loading={loading} icon={<BadgeCheck size={18} className="text-accent" />} />
          <Tile label={t.mod.pending} value={agg.pending} loading={loading} />
          <Tile label={t.verif.approved} value={agg.approved} loading={loading} tone="success" />
          <Tile label={t.verif.rejected} value={agg.rejected} loading={loading} tone="danger" />
        </div>

        <div className="max-w-md overflow-hidden rounded-md border border-border-default">
          <div className="border-b border-border-default bg-surface-2 px-3 py-2 text-xs font-medium text-tertiary">
            {t.verif.byKind}
          </div>
          {agg.byKind.map((k) => (
            <div key={k.kind} className="flex items-center justify-between border-b border-border-subtle px-3 py-2 text-sm last:border-0">
              <span className="text-secondary">{kindLabel[k.kind] ?? k.kind}</span>
              {loading ? <Skeleton className="h-4 w-10" /> : <span className="font-mono tabular text-primary">{k.count}</span>}
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

function Tile({
  label,
  value,
  loading,
  tone,
  icon,
}: {
  label: string
  value: number
  loading: boolean
  tone?: 'success' | 'danger'
  icon?: ReactNode
}) {
  const color = tone === 'success' ? 'text-success' : tone === 'danger' ? 'text-danger' : 'text-primary'
  return (
    <div className="inline-flex items-center gap-3 rounded-lg border border-border-default bg-surface-2 px-4 py-3">
      {icon}
      <div>
        <div className="text-xs text-tertiary">{label}</div>
        {loading ? <Skeleton className="mt-1 h-6 w-12" /> : <div className={`font-mono text-xl tabular ${color}`}>{value.toLocaleString()}</div>}
      </div>
    </div>
  )
}
